import { ApolloError } from "@apollo/client";
import { FC, ReactNode, createContext, useContext } from "react";
import { useParams } from "react-router-dom";
import {
  GetEoREmployeeProfileQuery,
  GetEoRMemberByUuidQuery,
  useGetEoREmployeeProfileQuery,
  useGetEoRMemberByUuidQuery,
  useGetMemberAccountSettingsQuery,
} from "types/generated/operations";
import { getEmployeeStatus } from "../components/HeaderSection/utils";

interface EoRProfileInfoContextType {
  profileInfo?: GetEoREmployeeProfileQuery;
  memberData?: GetEoRMemberByUuidQuery;
  memberCanonicalId?: string | null;
  employeeStatus?: string;
  loadingProfileInfo: boolean;
  error?: ApolloError;
  refetch?: () => Promise<unknown>;
}

interface EoRMemberContextProviderProps {
  children: ReactNode;
  memberUuid?: string;
}

const ProfileInfoContext = createContext<EoRProfileInfoContextType>({
  loadingProfileInfo: false,
});

export const ProfileInfoProvider: FC<EoRMemberContextProviderProps> = ({
  children,
  memberUuid,
}) => {
  const { id: idFromParams } = useParams();
  const { data: accountSettings } = useGetMemberAccountSettingsQuery();
  const id =
    idFromParams ?? accountSettings?.authenticatedMember.uuid ?? memberUuid;

  const {
    data: memberData,
    loading: loadingMemberData,
    error: memberDataError,
  } = useGetEoRMemberByUuidQuery({
    variables: { memberUuid: id },
    skip: !id,
  });

  // Fetch employee profile data based on member data
  const {
    data: profileInfo,
    loading: loadingProfileInfo,
    error: profileInfoLoadError,
    refetch,
  } = useGetEoREmployeeProfileQuery({
    variables: { memberId: memberData?.eorMember?.id },
    skip: !memberData?.eorMember.id,
  });

  const isLoading = loadingMemberData || loadingProfileInfo;
  const profileInfoError = memberDataError || profileInfoLoadError;
  const memberCanonicalId =
    profileInfo?.eorEmployeeProfile?.employment?.memberCanonicalId;

  return (
    <ProfileInfoContext.Provider
      value={{
        profileInfo: profileInfo,
        memberData: memberData,
        employeeStatus: getEmployeeStatus(profileInfo),
        loadingProfileInfo: isLoading,
        error: profileInfoError,
        memberCanonicalId: memberCanonicalId,
        refetch,
      }}
    >
      {children}
    </ProfileInfoContext.Provider>
  );
};

export const useProfileContextData = (forceFetch?: boolean) => {
  const context = useContext(ProfileInfoContext);

  if (forceFetch) {
    context.refetch?.();
  }

  return context;
};
